import { Search } from "lucide-react";
import { useState } from "react";

import {
  IgButton,
  IgCheckbox,
  IgLoading,
  IgRadio,
  IgTextField,
  IgTypography,
} from "@intelligems/ig-theme";
import { Box, Stack, useTheme } from "@mui/material";

import {
  ShopifyItemKind,
  useShopifyPickerItems,
} from "@src/components/MainApp/ShopifyItemPicker/useShopifyPickerItems";
import {
  SHOPIFY_ENTITY_COPY,
  ShopifyEntityPick,
} from "@src/features/ai/components/WorkflowEditor/execution/panel/shopifyEntityPickerCopy";

// A store with thousands of tags must never render thousands of rows; search
// narrows the rest, and the footer says so.
const MAX_RENDERED_ITEMS = 250;

/** Figma's menu height — about five and a half rows, so the list reads as scrollable. */
const MENU_HEIGHT = 182;

interface ShopifyEntityComboboxProps {
  entity: ShopifyItemKind;
  multiSelect: boolean;
  disabled: boolean;
  /** What was picked last time, so stepping back onto the card starts from it. */
  initialSelected: ShopifyEntityPick[];
  onConfirm: (picked: ShopifyEntityPick[]) => void;
}

/**
 * The Figma "Atlas / Chat combobox": the body a `shopify-pick` question renders
 * inside the survey card for a flat catalog kind (collections, tags, vendors,
 * product types). The whole list lives in the card above the composer — a
 * scrollable run of checkbox rows, then a footer holding the search field and
 * the explicit Confirm selections. No dialog: the card IS the picker, and the
 * card's own X is the way out.
 *
 * Layout is the spec verbatim (theme spacing unit is 4px): the menu inset 8px
 * left and 6px right with 32px rows padded 8px across and 6px down on a 2px
 * radius, a fade over the last row, and the footer padded 12px at the sides,
 * 8px above and 12px below. Search filters the loaded list client-side, which
 * is what the store-wide `useShopifyPickerItems` lists make possible.
 *
 * The row control follows the question: a `multiSelect` question assembles a
 * set from checkbox rows, while a single-answer question uses radio rows —
 * picking a second row replaces the first, which is what a checkbox row would
 * silently do while promising the opposite.
 */
export const ShopifyEntityCombobox = ({
  entity,
  multiSelect,
  disabled,
  initialSelected,
  onConfirm,
}: ShopifyEntityComboboxProps) => {
  const theme = useTheme();
  const copy = SHOPIFY_ENTITY_COPY[entity];
  const { items, loading } = useShopifyPickerItems(entity);

  const [search, setSearch] = useState("");
  const [selected, setSelected] =
    useState<ShopifyEntityPick[]>(initialSelected);

  const selectedIds = new Set(selected.map((item) => item.id));
  const normalizedSearch = search.trim().toLowerCase();
  const filtered = normalizedSearch
    ? items.filter((item) =>
        item.label.toLowerCase().includes(normalizedSearch)
      )
    : items;
  const rendered = filtered.slice(0, MAX_RENDERED_ITEMS);

  // Checkboxes assemble a set; a single-answer question offers radios, whose
  // pick always replaces the running one.
  const RowControl = multiSelect ? IgCheckbox : IgRadio;

  const toggleItem = (item: ShopifyEntityPick) => {
    setSelected((current) => {
      if (current.some((entry) => entry.id === item.id)) {
        return current.filter((entry) => entry.id !== item.id);
      }
      return multiSelect ? [...current, item] : [item];
    });
  };

  let menu;
  if (loading && items.length === 0) {
    menu = (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
        <IgLoading />
      </Stack>
    );
  } else if (filtered.length === 0) {
    menu = (
      <Stack alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
        <IgTypography variant="body2" sx={{ color: "text.secondary" }}>
          No {copy.plural} found
        </IgTypography>
      </Stack>
    );
  } else {
    menu = (
      <Stack sx={{ flex: 1, minWidth: 0, overflowY: "auto" }}>
        {rendered.map((item) => (
          <Stack
            key={item.id}
            component="label"
            direction="row"
            alignItems="center"
            gap={2}
            sx={{
              borderRadius: "2px",
              cursor: disabled ? "default" : "pointer",
              flexShrink: 0,
              minHeight: 32,
              px: 2,
              py: 1.5,
              "&:hover": {
                backgroundColor: disabled
                  ? "transparent"
                  : theme.igBackgroundColors.interactive.neutral.hover,
              },
            }}
          >
            <RowControl
              checked={selectedIds.has(item.id)}
              disabled={disabled}
              name={multiSelect ? undefined : `shopify-pick-${entity}`}
              onChange={() => toggleItem(item)}
              size="small"
              sx={{
                flexShrink: 0,
                p: 0,
                // The theme pins every checkbox and radio to a 32px hit box
                // with `!important`; the combobox row wants the 16px control
                // itself so the row keeps the height Figma gives it.
                "&.MuiButtonBase-root.MuiCheckbox-root, &.MuiButtonBase-root.MuiRadio-root":
                  {
                    height: "16px !important",
                    width: "16px !important",
                  },
                "& .MuiSvgIcon-root": { fontSize: 16 },
              }}
            />
            <IgTypography variant="body2" noWrap sx={{ minWidth: 0 }}>
              {item.label}
            </IgTypography>
          </Stack>
        ))}
        {filtered.length > rendered.length && (
          <IgTypography
            variant="body3"
            sx={{ color: "text.secondary", px: 2, py: 1.5 }}
          >
            Showing the first {MAX_RENDERED_ITEMS} of {filtered.length} matches
            — refine your search to see the rest.
          </IgTypography>
        )}
      </Stack>
    );
  }

  return (
    <>
      <Stack
        data-testid="shopify-entity-combobox"
        sx={{ height: MENU_HEIGHT, pl: 2, pr: 1.5 }}
      >
        {menu}
      </Stack>
      <Stack
        direction="row"
        gap={2}
        sx={{ position: "relative", px: 3, pt: 2, pb: 3 }}
      >
        {/* Figma's scroll fade: the last row dissolves into the footer rather
            than being clipped mid-glyph. */}
        <Box
          aria-hidden
          sx={{
            background: `linear-gradient(to bottom, transparent, ${theme.igBackgroundColors.baseLightHigh})`,
            height: 40,
            left: 0,
            pointerEvents: "none",
            position: "absolute",
            right: 0,
            top: -28,
          }}
        />
        <IgTextField
          fullWidth
          disabled={disabled}
          placeholder={copy.searchPlaceholder}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <Box
                  sx={{ display: "flex", mr: 1 }}
                  aria-hidden
                  data-testid="shopify-entity-combobox-search-icon"
                >
                  <Search size={16} color={theme.igIconColors.light} />
                </Box>
              ),
            },
          }}
          sx={{ flex: 1, minWidth: 0 }}
        />
        <IgButton
          variant="outlined"
          disabled={disabled || selected.length === 0}
          onClick={() => onConfirm(selected)}
          sx={{ flexShrink: 0, height: 36 }}
        >
          Confirm selections
        </IgButton>
      </Stack>
    </>
  );
};
